// Menu de cuenta de la cabecera: con que cuenta se ingreso, en que plantas
// esta habilitada la empresa, los avisos de oferta nueva (mail, PP-68; sonido
// con el portal abierto, PP-71) y Salir. Es un panel que se despliega
// (disclosure), no un role="menu": adentro hay texto y una lista, no solo
// acciones. Se cierra con Escape o con un click afuera, como el menu del
// encabezado de la torre (apps/tower/src/tower/HeaderMenu.tsx).
//
// Los interruptores son checkboxes con role="switch" dibujados como
// interruptor (sin aspecto nativo): el lector de pantalla y el teclado ven un
// control conocido; la vista, el del design system.
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button.js";
import { cn } from "../components/ui/cn.js";
import { siteOptions } from "../logic/navigation.js";
import type { EstadoDePlantas } from "../logic/sites.js";
import type { AvisoDeOfertas } from "../notifications/useOfferMail.js";

interface Props {
  user: string;
  plantas: EstadoDePlantas;
  onLogout: () => void;
  /** Aviso de oferta nueva por mail; sin el, el menu no lo muestra. */
  aviso?: AvisoDeOfertas;
  onCambiarAviso?: (activo: boolean) => void;
  /** Aviso sonoro de oferta nueva, `true` encendido; sin el, el menu no lo muestra. */
  sonido?: boolean;
  onCambiarSonido?: (encendido: boolean) => void;
}

/** Iniciales del email para el avatar: dos letras, nunca el email entero en el circulo. */
function initials(user: string): string {
  const local = user.split("@")[0] ?? user;
  const parts = local.split(/[._-]+/).filter((p) => p.length > 0);
  const letters = parts.length >= 2 ? `${parts[0]![0]!}${parts[1]![0]!}` : local.slice(0, 2);
  return letters.toUpperCase();
}

export function AccountMenu({ user, plantas, onLogout, aviso, onCambiarAviso, sonido, onCambiarSonido }: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      const root = rootRef.current;
      if (root !== null && e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        title="Cuenta"
        onClick={() => {
          setOpen(!open);
        }}
        className="flex h-9 cursor-pointer items-center gap-2 rounded-md pl-2 pr-1 text-xs text-texto-2 hover:bg-velo"
      >
        {/* En el celular solo el avatar; el email sigue siendo el nombre accesible. */}
        <span className="sr-only max-w-[220px] truncate sm:not-sr-only">{user}</span>
        <span aria-hidden="true" className="grid size-7 place-items-center rounded-full border border-linea bg-elevada text-micro font-semibold text-texto-2">
          {initials(user)}
        </span>
      </button>

      {open && (
        <div
          id={panelId}
          className="entrada-sube absolute right-0 top-full z-40 mt-2 flex w-[min(300px,calc(100vw-1.5rem))] flex-col gap-3.5 rounded-lg border border-linea bg-superficie p-4 shadow-panel"
        >
          <div>
            <p className="rotulo m-0 text-texto-suave">Cuenta</p>
            <p className="m-0 mt-1 break-all text-sm text-texto">{user}</p>
          </div>
          <div>
            <p className="rotulo m-0 text-texto-suave">Plantas habilitadas</p>
            {plantas.estado === "listo" ? (
              plantas.plantas.length === 0 ? (
                <p className="m-0 mt-1 text-sm text-texto-suave">Ninguna todavía.</p>
              ) : (
                <ul aria-label="Plantas habilitadas" className="m-0 mt-1 list-none p-0 text-sm text-texto">
                  {siteOptions(plantas.plantas).map((option) => (
                    <li key={option.siteId}>{option.name}</li>
                  ))}
                </ul>
              )
            ) : (
              <p className="m-0 mt-1 text-sm text-texto-suave">
                {plantas.estado === "cargando" ? "Consultando..." : "No disponible."}
              </p>
            )}
            <p className="m-0 mt-1 text-xs leading-normal text-texto-debil">
              Al dejar de operar en una planta, deja de aparecer acá automáticamente.
            </p>
          </div>
          {(aviso !== undefined || sonido !== undefined) && (
            <div className="flex flex-col gap-2.5">
              <p className="rotulo m-0 text-texto-suave">Avisos</p>
              {sonido !== undefined && <AvisoSonoro encendido={sonido} onCambiar={onCambiarSonido} />}
              {aviso !== undefined && <AvisoPorMail aviso={aviso} onCambiar={onCambiarAviso} />}
            </div>
          )}
          <Button variant="secundario" size="sm" onClick={onLogout}>
            Salir
          </Button>
        </div>
      )}
    </div>
  );
}

/** Checkbox con role="switch" dibujado como interruptor: 36x20, acero claro encendido. */
function Switch({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <input
      type="checkbox"
      role="switch"
      className={cn(
        "relative mt-0.5 h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-pill border-0 bg-elevada transition-colors duration-150 ease-estandar",
        "before:absolute before:left-0.5 before:top-0.5 before:size-4 before:rounded-full before:bg-blanco before:transition-transform before:duration-150 before:ease-estandar before:content-['']",
        "checked:bg-acero-claro checked:before:translate-x-4 disabled:cursor-default disabled:opacity-45",
        className,
      )}
      {...props}
    />
  );
}

function AvisoSonoro({ encendido, onCambiar }: { encendido: boolean; onCambiar: ((encendido: boolean) => void) | undefined }): React.JSX.Element {
  const ayudaId = useId();
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-2.5 text-sm text-texto">
        <Switch
          aria-checked={encendido}
          aria-describedby={ayudaId}
          checked={encendido}
          disabled={onCambiar === undefined}
          onChange={(e) => {
            onCambiar?.(e.target.checked);
          }}
        />
        <span>Sonido por cada oferta nueva</span>
      </label>
      <p id={ayudaId} className="m-0 mt-1 pl-[46px] text-xs leading-normal text-texto-suave">
        Con el portal abierto, en cualquier sección. Lo recuerda este navegador.
      </p>
    </div>
  );
}

function AvisoPorMail({ aviso, onCambiar }: { aviso: AvisoDeOfertas; onCambiar: ((activo: boolean) => void) | undefined }): React.JSX.Element {
  const ayudaId = useId();
  return (
    <div>
      {aviso.estado === "listo" ? (
        <>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-texto">
            <Switch
              aria-checked={aviso.activo}
              aria-describedby={ayudaId}
              checked={aviso.activo}
              disabled={aviso.guardando || onCambiar === undefined}
              onChange={(e) => {
                onCambiar?.(e.target.checked);
              }}
            />
            <span>Mail por cada oferta nueva</span>
          </label>
          <p id={ayudaId} className="m-0 mt-1 pl-[46px] text-xs leading-normal text-texto-suave">
            {aviso.fallo
              ? "No se pudo guardar el cambio. Reintentar en unos minutos."
              : aviso.guardando
                ? "Guardando..."
                : "Llega a la casilla de esta cuenta con planta, horario y plazo, sin datos de pasajeros."}
          </p>
        </>
      ) : (
        <p className="m-0 text-sm text-texto-suave">{aviso.estado === "cargando" ? "Consultando..." : "No disponible."}</p>
      )}
    </div>
  );
}
